import { Injectable, ViewContainerRef, inject, signal } from '@angular/core';
import {
  NotificationRef,
  NotificationService as KendoNotificationService,
} from '@progress/kendo-angular-notification';
import { NotificationComponent } from '../components/notification/notification.component';
import { isNullOrUndefined } from 'is-what';
import { TranslateService } from '@ngx-translate/core';
import { NotificationTypeEnum } from '../enums/notificationType.enum';
import { interval } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private kendoNotificationService = inject(KendoNotificationService);
  private translateService = inject(TranslateService);

  private apiCount = 0;
  private isLoadingSpinnerSignal = signal<boolean>(false);
  readonly isLoadingSpinner = this.isLoadingSpinnerSignal.asReadonly();

  private isLoadingCVNotificationSignal = signal<boolean>(false);
  readonly isLoadingCVNotification =
    this.isLoadingCVNotificationSignal.asReadonly();

  constructor() {
    this.checkQuarterlyNotification();
  }

  /**
   * The function `show` displays a notification with specified type, message, duration, closability, and
   * container.
   *
   * @param {NotificationTypeEnum} type
   * @param {string} [message]
   * @param {number} [time=3000]
   * @param {boolean} [isClosable=false]
   * @param {ViewContainerRef} [container]
   * @memberof NotificationService
   */
  show(
    type: NotificationTypeEnum,
    message?: string,
    time = 3000,
    isClosable = false,
    container?: ViewContainerRef,
  ): void {
    const notificationRef: NotificationRef = this.kendoNotificationService.show(
      {
        content: NotificationComponent,
        hideAfter: time,
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: time * 0.3 },
        type: { style: type, icon: true },
        closable: isClosable,
        appendTo: container,
      },
    );

    if (notificationRef) {
      notificationRef.content!.instance.header = this.translateService.instant(
        'common.notifications.header.' + type,
      );

      if (!isNullOrUndefined(message)) {
        notificationRef.content!.instance.message = message;
      }

      if (time > 0) {
        window.setTimeout(() => {
          notificationRef.hide();
        }, time);
      }
    }
  }
  /**
   *The function setLoading toggles the visibility of a loader based on the isActive parameter.
   *
   * @param {boolean} isActive
   * @memberof NotificationService
   */
  setLoading(isActive: boolean): void {
    if (isActive) {
      this.showLoader();
    } else {
      this.hideLoader();
    }
  }

  /**
   * The function `showLoader` increments the `apiCount` variable and sets the `isLoadingSpinnerSignal`
   * to true if `apiCount` is 0.
   */
  private showLoader() {
    if (this.apiCount === 0) {
      Promise.resolve().then(() => this.isLoadingSpinnerSignal.set(true));
    }
    this.apiCount++;
  }

  /**
   * The `hideLoader` function decreases the `apiCount` and sets `isLoadingSpinnerSignal` to false when
   * `apiCount` reaches 0.
   */
  private hideLoader() {
    this.apiCount--;
    if (this.apiCount === 0) {
      Promise.resolve().then(() => this.isLoadingSpinnerSignal.set(false));
    }
  }

  private checkQuarterlyNotification() {
    this.runCvNotification();
    interval(3600).subscribe(() => {
      this.runCvNotification();
    });
  }

  /**
   * Evaluates whether the current date falls within the configured quarterly CV
   * notification window and updates the reactive notification state accordingly.
   *
   * Process:
   * 1. Reads today's calendar day and month.
   * 2. Uses `isInQuarterlyWindow(day, month)` to determine eligibility.
   * 3. Sets `isLoadingCVNotificationSignal` to true if within the window; otherwise false.
   *
   * Side Effects:
   * - Mutates the `isLoadingCVNotificationSignal` state/signal.
   *
   * When to call:
   * - On application initialization, user login, or scheduled refresh points to keep
   *   the CV notification visibility in sync with the current date.
   *
   * Related:
   * - `isInQuarterlyWindow(day: number, month: number): boolean` (encapsulates window logic)
   * - `isLoadingCVNotificationSignal` (reactive flag controlling CV notification display/loading)
   */
  runCvNotification(): void {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth() + 1;

    const isNotificationPeriod = this.isInQuarterlyWindow(
      currentDay,
      currentMonth,
    );
    this.isLoadingCVNotificationSignal.set(isNotificationPeriod);
  }

  private isInQuarterlyWindow(day: number, month: number): boolean {
    const quarterlyWindows = [
      { start: { month: 1, day: 1 }, end: { month: 1, day: 7 } },
      { start: { month: 4, day: 1 }, end: { month: 4, day: 7 } },
      { start: { month: 7, day: 1 }, end: { month: 7, day: 7 } },
      { start: { month: 10, day: 1 }, end: { month: 10, day: 7 } },
    ];

    const today = new Date();
    const dayOfWeek = today.getDay();

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false;
    }

    const matchingWindow = quarterlyWindows.find((quarterlyWindow) => {
      return (
        month === quarterlyWindow.start.month &&
        day >= quarterlyWindow.start.day &&
        day <= quarterlyWindow.end.day
      );
    });

    if (matchingWindow) {
      const isMissedWeekend =
        (dayOfWeek === 1 && day <= matchingWindow.end.day + 2) ||
        (dayOfWeek === 2 && day === matchingWindow.end.day + 1);
      return isMissedWeekend || true;
    }

    return false;
  }

  private isInWeeklyWindow(): boolean {
    const today = new Date();
    const dayOfWeek = today.getDay();

    return dayOfWeek >= 1 && dayOfWeek <= 2;
  }
}
